"use client"

/**
 * The new booking's look, for the builder (Eugene, 30.09: "the style of the
 * new booking"). Ported from the membership-first booking (Eugene's
 * prototype, ahead-booking-membership-first on `eugene/membership-first-v2`):
 * - SelectionCheck: add-ons-step/SelectionCheck.tsx, the pick mark.
 * - DetailsPill: membership-first/DetailsPill.tsx, the grey "More info" pill.
 * - InfoSheet: product-step/card/ProductUSPs.tsx, the details popup, with
 *   popupChrome.ts's frost, card motion and outside close button, and
 *   IOSBottomSheet.tsx on a phone.
 *
 * The site has the booking's palette (deep-blue-900, light-blue, neutral-50),
 * so only the booking's shadows are written out here.
 *
 * What a popup says comes from the site: the product data behind today's
 * pricing popups (`data/product-info.ts` and the `pricing` strings), the CMS
 * biomarkers and the live prices, read by the page. The booking's own copy
 * lives in its Supabase rows, which the site cannot read.
 */

import Image from "next/image"
import { m, AnimatePresence } from "framer-motion"
import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type SyntheticEvent,
} from "react"
import { createPortal } from "react-dom"
import {
  CheckCircleNeutralIcon,
  CheckmarkIcon,
  ChevronDownIcon,
  CloseIcon,
  InfoContrastIcon,
  InfoForWhomIcon,
  InfoHealthAreasIcon,
  InfoPreparationIcon,
  InfoRadiationIcon,
  InfoTimeIcon,
  PacemakerIcon,
  PersonHeartIcon,
} from "@/components/ui/icons"
import type {
  BiomarkerGroup,
  ProductInfoData,
} from "@/components/ui/product-info-modal"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"
import { PLAN_MARK_BG, type PictureKind } from "./pricing-v5-items"

/** The booking's `shadow-card-hairline`: a white card set off by a hairline. */
export const HAIRLINE =
  "shadow-[0_0_1.4px_rgba(0,0,0,0.04),0_0_0.2px_rgba(0,0,0,0.1)]"

/** The booking's `shadow-select-glow`, which replaces the hairline when picked. */
export const SELECT_GLOW =
  "shadow-[0_0_0_3px_rgba(115,157,231,0.12),0_0_8px_rgba(115,157,231,0.18),0_0_1.4px_rgba(0,0,0,0.04),0_0_0.2px_rgba(0,0,0,0.1)]"

/** The booking's `shadow-card-lift`, faded in under a tile on hover. */
export const LIFT =
  "shadow-[0_2px_16px_rgba(106,133,161,0.04),0_8px_32px_rgba(106,133,161,0.04)]"

/** The frost behind a popup (popupChrome.ts `POPUP_FROST_CLASS`). */
const FROST = "bg-neutral-950/20 backdrop-blur-md backdrop-saturate-150"

/** The booking's rounded solid tick (icons/CheckmarkIcon.tsx). */
function SolidCheck({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      className={className}
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  )
}

/**
 * A bookmark, outlined, or filled once the item is saved: "Save" keeps an
 * item for the check-up without booking it (Eugene, 01.10).
 */
export function BookmarkIcon({
  filled = false,
  className,
}: {
  filled?: boolean
  className?: string
}) {
  // Rounded corners and a soft notch; the filled one has no stroke, so
  // its edge stays smooth at small sizes (Eugene, 01.10: "jagged"). The
  // outline's 1.8 matches the navbar's login icon.
  const d =
    "M6.5 3h11a2 2 0 0 1 2 2v15.1a.8.8 0 0 1-1.24.67L12 16.9l-6.26 3.87A.8.8 0 0 1 4.5 20.1V5a2 2 0 0 1 2-2Z"
  return (
    <svg aria-hidden className={className} viewBox="0 0 24 24">
      {filled ? (
        <path d={d} fill="currentColor" />
      ) : (
        <path
          d={d}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
          strokeLinejoin="round"
        />
      )}
    </svg>
  )
}

/**
 * The pick mark: a heavily rounded 24px square for the tests, where several
 * can be picked, and a circle for the MRI packages, where one at most can.
 * It floods deep blue from the middle when picked. `className` places it,
 * position included, as in the booking. Its ring darkens while the pointer
 * is on the `group` it picks, the only hover a table row has.
 */
export function SelectionCheck({
  selected,
  round = false,
  className,
}: {
  selected: boolean
  round?: boolean
  className: string
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-6 shrink-0 items-center justify-center overflow-hidden bg-white transition-colors duration-200",
        round ? "rounded-full" : "rounded-[10px]",
        selected ? "text-white" : "text-transparent",
        className
      )}
    >
      {/* The fill blooms from the middle rather than swapping colour, and
          the ring is painted over it on the same timing. */}
      <span
        className={cn(
          "bg-deep-blue-900 pointer-events-none absolute inset-0 m-auto block size-6 rounded-full transition-[transform,opacity] duration-300 ease-out motion-reduce:transition-none",
          selected ? "scale-150 opacity-100" : "scale-75 opacity-0"
        )}
      />
      <span
        className={cn(
          "pointer-events-none absolute inset-0 border-[1.5px] transition-colors duration-300 ease-out motion-reduce:transition-none",
          round ? "rounded-full" : "rounded-[10px]",
          selected
            ? "border-deep-blue-900"
            : "border-neutral-300 group-hover:border-neutral-500"
        )}
      />
      <SolidCheck
        className={cn(
          "relative size-[18px] text-white transition-opacity duration-150 ease-out motion-reduce:transition-none",
          selected ? "opacity-100 delay-100" : "opacity-0"
        )}
      />
    </span>
  )
}

/**
 * "More info": the booking's 24px grey pill (28px beside larger copy) on a
 * 40px button.
 */
export function DetailsPill({
  label,
  onClick,
  size = "md",
  tone = "light",
  className,
}: {
  label: string
  onClick: () => void
  /** `mini`: a small button, 32px, beside Save in a test's row. */
  size?: "md" | "mini" | "lg"
  /** `glass` for a pill on the plan card's photograph. */
  tone?: "light" | "glass"
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      aria-haspopup="dialog"
      className={cn(
        "group/info relative z-10 inline-flex min-h-[40px] w-fit shrink-0 items-center",
        className
      )}
    >
      <span
        className={cn(
          "inline-flex items-center rounded-full transition-colors duration-200",
          tone === "glass"
            ? "bg-white/15 text-white group-hover/info:bg-white/25"
            : "bg-neutral-50 text-neutral-600 group-hover/info:bg-neutral-100 group-hover/info:text-neutral-800",
          size === "lg"
            ? "type-title-md h-9 px-4"
            : size === "mini"
              ? "type-title-sm h-8 px-3.5"
              : "type-title-sm h-6 px-3"
        )}
      >
        {label}
      </span>
    </button>
  )
}

type AddLabels = Pick<InfoLabels, "add" | "added" | "saveTip" | "savedTip">

/**
 * A small dark tip that shows while the pointer is on its `group/tip`
 * parent or the parent has focus (Eugene, 01.10: "tooltips on hover").
 * `align` keeps it inside the card when its button sits in a corner.
 */
export function HoverTip({
  label,
  side = "top",
  align = "center",
}: {
  label?: string
  side?: "top" | "bottom"
  align?: "center" | "end"
}) {
  if (!label) return null
  return (
    <span
      role="tooltip"
      className={cn(
        "type-body-sm pointer-events-none absolute z-20 rounded-lg bg-neutral-900 px-2.5 py-1.5 whitespace-nowrap text-white opacity-0 transition-opacity duration-150 group-hover/tip:opacity-100 group-focus-visible/tip:opacity-100",
        side === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5",
        align === "center" ? "left-1/2 -translate-x-1/2" : "right-0"
      )}
    >
      {label}
    </span>
  )
}

/**
 * "Save" as a mini button the size of "More info" (`mini`), to sit beside
 * it in a test's row (Eugene, 01.10). Saved, its bookmark fills and it reads "Saved".
 */
export function SavePill({
  picked,
  onToggle,
  labels,
  name,
}: {
  picked: boolean
  onToggle: () => void
  labels: AddLabels
  /** Heard after the label, as a screen reader may reach it out of its row. */
  name: string
}) {
  return (
    <button
      type="button"
      aria-pressed={picked}
      onClick={onToggle}
      className="group/save group/tip relative z-10 inline-flex min-h-[40px] w-fit shrink-0 items-center"
    >
      <HoverTip label={picked ? labels.savedTip : labels.saveTip} />
      <span
        className={cn(
          // White until hovered, the bookmark grey (Eugene, 01.10).
          "type-title-sm inline-flex h-8 items-center gap-1.5 rounded-full bg-white pr-3.5 pl-2.5 transition-colors duration-200 group-hover/save:bg-neutral-50",
          picked
            ? "text-neutral-900"
            : "text-neutral-600 group-hover/save:text-neutral-800"
        )}
      >
        {/* Filled grey, blue once saved (Eugene, 01.10). */}
        <BookmarkIcon
          filled
          className={cn(
            "size-4",
            picked ? "text-deep-blue-700" : "text-neutral-200"
          )}
        />
        {picked ? labels.added : labels.add}
      </span>
      <span className="sr-only"> {name}</span>
    </button>
  )
}
type AddSize = "sm" | "md"

function addBox(size: AddSize) {
  return cn(
    "inline-flex shrink-0 items-center justify-center rounded-full border",
    size === "md"
      ? "type-title-md h-11 min-w-[100px] px-4"
      : "type-title-sm h-9 min-w-[88px] px-3"
  )
}

/**
 * Both labels in one cell, the one not in use hidden, so the button is as
 * wide as its longer word in either state ("Hinzufügen", "Hinzugefügt") and
 * the prices beside it stay put.
 */
function AddFace({ picked, labels }: { picked: boolean; labels: AddLabels }) {
  const face = "col-start-1 row-start-1 inline-flex items-center gap-1.5"
  return (
    <span className="grid justify-items-center">
      <span className={cn(face, picked && "invisible")}>
        <BookmarkIcon className="size-4" />
        {labels.add}
      </span>
      <span className={cn(face, !picked && "invisible")}>
        <BookmarkIcon filled className="size-4" />
        {labels.added}
      </span>
    </span>
  )
}

/**
 * The page's Save button (it read "Add" until 01.10). Saving keeps an item
 * for the check-up without booking it: the scans book straight away, the
 * add-ons go with the Baseline plan, so a cart was the wrong promise
 * (Eugene, 01.10). An item is saved by pressing Save, not by a tap anywhere
 * on its card (30.09); a second press takes it out.
 */
export function AddButton({
  picked,
  onToggle,
  labels,
  name,
  size = "sm",
  iconOnly = false,
  className,
}: {
  picked: boolean
  onToggle: () => void
  labels: AddLabels
  /** Heard after the label, as a screen reader may reach it out of its row. */
  name: string
  size?: AddSize
  /** Just the bookmark, a circle, where a row has no room for the word. */
  iconOnly?: boolean
  className?: string
}) {
  if (iconOnly) {
    return (
      <button
        type="button"
        aria-pressed={picked}
        aria-label={`${picked ? labels.added : labels.add} ${name}`}
        onClick={onToggle}
        // Just the bookmark, filled, with no ground or ring; saved, it
        // turns dark (Eugene, 01.10).
        className={cn(
          "group/tip focus-visible:outline-deep-blue-900 relative inline-flex shrink-0 items-center justify-center rounded-full transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2",
          size === "md" ? "size-11" : "size-9",
          // The grey of the More info pill; blue once saved (Eugene, 01.10).
          picked
            ? "text-deep-blue-700 hover:text-deep-blue-800"
            : "text-neutral-100 hover:text-neutral-300",
          className
        )}
      >
        <BookmarkIcon filled className="size-6" />
        <HoverTip
          label={picked ? labels.savedTip : labels.saveTip}
          side="bottom"
          align="end"
        />
      </button>
    )
  }
  return (
    <button
      type="button"
      aria-pressed={picked}
      onClick={onToggle}
      className={cn(
        addBox(size),
        "focus-visible:outline-deep-blue-900 transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2",
        // A quiet grey pill, as More info; saved, the bookmark fills and
        // the word turns to "Saved" (Eugene, 01.10: "calm, grey").
        "border-transparent bg-neutral-100 text-neutral-900 hover:bg-neutral-200/60",
        className
      )}
    >
      <AddFace picked={picked} labels={labels} />
      <span className="sr-only"> {name}</span>
    </button>
  )
}

/**
 * An Add button's width and nothing else, for a header whose last column
 * has to end where a row's prices end, next to their buttons.
 */
export function AddButtonSpace({
  labels,
  size = "sm",
  className,
}: {
  labels: AddLabels
  size?: AddSize
  className?: string
}) {
  return (
    <span
      aria-hidden
      className={cn(
        addBox(size),
        "invisible h-0 overflow-hidden border-transparent",
        className
      )}
    >
      <AddFace picked={false} labels={labels} />
    </span>
  )
}

/** A picture that hides itself if the file does not load, as in the booking. */
function hideOnError(e: SyntheticEvent<HTMLImageElement>) {
  e.currentTarget.style.display = "none"
}

/**
 * The small picture beside a test, a scan or the plan, as the booking sets
 * it: a tube or an illustration fills its rounded plate (each file carries
 * its own ground), a scanner render is set whole on the card's white.
 * `className` sizes the plate; `px` is its largest size, for the image
 * request.
 */
export function ItemPicture({
  src,
  px,
  kind,
  className,
}: {
  src: string | undefined
  px: number
  kind: PictureKind
  className: string
}) {
  if (!src) return null
  return (
    <span
      className={cn(
        "relative block shrink-0 overflow-hidden rounded-[8px] bg-white",
        className
      )}
    >
      <Image
        src={src}
        alt=""
        fill
        sizes={`${px}px`}
        onError={hideOnError}
        className={kind === "render" ? "object-contain" : "object-cover"}
      />
    </span>
  )
}

/** The Ahead mark, the white arrow of the booking's plan mark. */
function AheadArrow({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 15 16" fill="none" aria-hidden className={className}>
      <path
        d="M11.3742 0H0V1.99316L11.2938 1.71601C12.3158 1.69133 13.1522 2.51707 13.1311 3.53073L12.8861 15.7934H14.8593V3.4586C14.8593 1.54897 13.2976 0 11.3723 0Z"
        fill="white"
      />
      <path
        d="M9.37226 4.30513L0 14.3525L1.42584 15.8028L10.8517 5.66047C11.2325 5.25045 11.2019 4.61264 10.7828 4.24059C10.3732 3.87612 9.74546 3.9046 9.37226 4.30513Z"
        fill="white"
      />
    </svg>
  )
}

/**
 * The plan's own picture, the booking's PlanMark: the Ahead mark in white
 * on the membership gradient, half the tile's height, so the tile can be
 * any size. `className` sizes and rounds it.
 */
export function PlanMark({ className }: { className: string }) {
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden bg-cover bg-center",
        className
      )}
      style={{ backgroundImage: `url(${PLAN_MARK_BG})` }}
    >
      <AheadArrow className="h-1/2 w-auto" />
    </span>
  )
}

/* ── What a popup shows ────────────────────────────────────────────────── */

type InfoRowKey =
  | "healthAreas"
  | "timeNeeded"
  | "radiation"
  | "contrastDye"
  | "preparation"
  | "forWhom"

export type InfoContent = {
  name: string
  image?: string
  /** A picture of the popup's own in place of `image`, such as the plan mark. */
  picture?: ReactNode
  /** "Time needed: 15 min", "No radiation", "No contrast dye" */
  meta: string[]
  /** What it costs here. `was` is the price without the plan, struck. */
  price?: { now: string; was?: string; wasTip?: string; after?: string }
  /** A note above the description, in the booking's light-blue banner. */
  banner?: string
  description: string
  keyBenefits?: string[]
  whoIsItFor?: string[]
  /** Sections of the popup's own, set after the benefits. */
  extra?: ReactNode
  whatYouGet?: { heading?: string; items: string[] }
  scanAreas?: string[]
  biomarkers?: BiomarkerGroup[]
  info?: { key: InfoRowKey; label: string; value: string }[]
}

const INFO_ROWS: { key: InfoRowKey; label: string }[] = [
  { key: "healthAreas", label: "piLabelHealthAreas" },
  { key: "timeNeeded", label: "piLabelTimeNeeded" },
  { key: "radiation", label: "piLabelRadiation" },
  { key: "contrastDye", label: "piLabelContrastDye" },
  { key: "preparation", label: "piLabelPreparation" },
  { key: "forWhom", label: "piLabelForWhom" },
]

const INFO_ICONS: Record<
  InfoRowKey,
  (props: { className?: string; size?: number }) => ReactNode
> = {
  healthAreas: InfoHealthAreasIcon,
  timeNeeded: InfoTimeIcon,
  radiation: InfoRadiationIcon,
  contrastDye: InfoContrastIcon,
  preparation: InfoPreparationIcon,
  forWhom: InfoForWhomIcon,
}

/**
 * A product's popup content, read the way today's pricing popups read it
 * (`resolveProductInfo` in components/ui/product-info-modal.tsx). `extras`
 * replaces any part, such as a description written for the plan flow.
 */
export function useInfoContent() {
  const t = useTranslations("pricing") as unknown as (key: string) => string
  return (
    data: ProductInfoData,
    extras: Partial<InfoContent> = {}
  ): InfoContent => {
    const duration = data.durationKey ? t(data.durationKey) : undefined
    const info = INFO_ROWS.flatMap(({ key, label }) => {
      const valueKey = data.infoKeys?.[key]
      return valueKey ? [{ key, label: t(label), value: t(valueKey) }] : []
    })
    return {
      name: t(data.nameKey),
      // The new booking's picture where today's site has another.
      image: data.membershipFirstImage ?? data.image,
      meta: [
        duration && `${t("piLabelTimeNeeded")}: ${duration}`,
        data.radiationKey && t(data.radiationKey),
        data.contrastDyeKey && t(data.contrastDyeKey),
      ].filter((part): part is string => Boolean(part)),
      description: t(data.descKey),
      keyBenefits: data.benefitKeys?.map((key) => t(key)),
      whoIsItFor: data.audienceKeys?.map((key) => t(key)),
      whatYouGet: data.whatYouGetKeys
        ? { items: data.whatYouGetKeys.map((key) => t(key)) }
        : undefined,
      scanAreas: data.scanAreaKeys?.map((key) => t(key)),
      info: info.length > 0 ? info : undefined,
      ...extras,
    }
  }
}

/* ── The popup's parts ─────────────────────────────────────────────────── */

/**
 * The plan card's tick, a filled green disc, so the popup's lines read as
 * the card's (Eugene, 30.09).
 */
function CheckDisc() {
  return (
    <CheckCircleNeutralIcon
      size={22}
      aria-hidden
      className="text-deep-green-500 shrink-0"
    />
  )
}

/** lucide's "info", the booking's icon for the Information heading. */
function InfoOutline({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn("size-6 shrink-0", className)}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  )
}

export function SectionHeading({ children }: { children: ReactNode }) {
  return <p className="type-title-md py-2 text-neutral-900">{children}</p>
}

type Tile = { icon: ReactNode; title: string; items: string[] }

/**
 * Key benefits and who it is for. A test's pair are grey cards side by side,
 * 2px apart with 4px corners where they meet; a package's are open sections,
 * as in the booking.
 */
function Tiles({ tiles, cards }: { tiles: Tile[]; cards: boolean }) {
  const last = tiles.length - 1
  return (
    <div
      className={
        cards
          ? "flex flex-col gap-0.5 sm:flex-row"
          : "flex flex-col gap-4 sm:gap-6"
      }
    >
      {tiles.map((tile, i) => (
        <div
          key={tile.title}
          className={
            cards
              ? cn(
                  "flex min-w-0 flex-1 flex-col gap-3 bg-neutral-50 p-6",
                  i === 0
                    ? "rounded-tl-2xl rounded-tr-2xl"
                    : "rounded-tl rounded-tr",
                  i === last ? "sm:rounded-tr-2xl" : "sm:rounded-tr",
                  i === last ? "rounded-bl-2xl" : "rounded-bl",
                  i === 0 ? "sm:rounded-bl-2xl" : "sm:rounded-bl",
                  i === last ? "rounded-br-2xl" : "rounded-br"
                )
              : "flex flex-col gap-1"
          }
        >
          <div className={cn("flex items-center gap-2", !cards && "py-2")}>
            <span className="shrink-0 text-neutral-900">{tile.icon}</span>
            <p className="type-title-md text-neutral-900">{tile.title}</p>
          </div>
          {/* A list when there is more than one line, as in the booking. */}
          {tile.items.length > 1 ? (
            <ul className="type-body-lg flex list-disc flex-col gap-1 pl-4 text-neutral-900">
              {tile.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : (
            <p className="type-body-lg text-neutral-900">{tile.items[0]}</p>
          )}
        </div>
      ))}
    </div>
  )
}

/** Ten biomarkers, then "View more", as in the booking. */
const BIOMARKERS_SHOWN = 10

function Biomarkers({
  groups,
  heading,
  more,
  less,
}: {
  groups: BiomarkerGroup[]
  heading: string
  more: string
  less: string
}) {
  const [open, setOpen] = useState(false)
  const all = groups.flatMap((group) =>
    group.items.map((item) => ({ category: group.category, item }))
  )
  const shown = open ? all : all.slice(0, BIOMARKERS_SHOWN)

  const rows: ReactNode[] = []
  shown.forEach((entry, i) => {
    if (i === 0 || entry.category !== shown[i - 1].category) {
      if (i > 0) {
        rows.push(
          <div key={`rule-${i}`} className="py-1 sm:col-span-2">
            <div className="h-px bg-neutral-200" />
          </div>
        )
      }
      rows.push(
        <p
          key={`category-${i}`}
          className="type-title-sm pt-3.5 pb-1.5 text-neutral-900 sm:col-span-2"
        >
          {entry.category}
        </p>
      )
    }
    rows.push(
      <p
        key={`item-${i}`}
        className="type-body-md flex h-9 items-center pr-4 text-neutral-900"
      >
        {entry.item}
      </p>
    )
  })

  return (
    <div className="flex flex-col gap-1">
      <SectionHeading>{heading}</SectionHeading>
      <div className="grid grid-cols-1 sm:grid-cols-2">{rows}</div>
      {all.length > BIOMARKERS_SHOWN && (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="mt-1 flex items-center justify-center gap-1 p-1"
        >
          <span aria-hidden className="h-px flex-1 bg-neutral-200" />
          <span className="type-title-sm px-1 whitespace-nowrap text-neutral-900">
            {open ? less : more}
          </span>
          <ChevronDownIcon
            width={20}
            height={20}
            className={cn(
              "text-neutral-900 transition-transform duration-200",
              open && "rotate-180"
            )}
          />
          <span aria-hidden className="h-px flex-1 bg-neutral-200" />
        </button>
      )}
    </div>
  )
}

function InfoBody({
  content,
  tileCards,
  titleId,
}: {
  content: InfoContent
  tileCards: boolean
  titleId: string
}) {
  const t = useTranslations("pricing")
  const {
    name,
    image,
    picture,
    meta,
    price,
    banner,
    description,
    keyBenefits,
    whoIsItFor,
    extra,
    whatYouGet,
    scanAreas,
    biomarkers,
    info,
  } = content
  // Today's popups write "Key benefits:"; the booking's heading has no colon.
  const bare = (heading: string) => heading.replace(/:$/, "")
  const tiles: Tile[] = []
  if (keyBenefits?.length) {
    tiles.push({
      icon: <PacemakerIcon size={24} />,
      title: bare(t("piSectionKeyBenefits")),
      items: keyBenefits,
    })
  }
  if (whoIsItFor?.length) {
    tiles.push({
      icon: <PersonHeartIcon size={24} />,
      title: bare(t("piSectionWhoIsItFor")),
      items: whoIsItFor,
    })
  }

  return (
    <div className="flex flex-col gap-4 px-4 sm:gap-6 sm:px-8">
      <div className="flex items-start gap-4">
        {picture ??
          (image && (
            <span className="relative size-[92px] shrink-0 overflow-hidden rounded-2xl bg-neutral-50 sm:size-[131px]">
              <Image
                src={image}
                alt=""
                fill
                sizes="262px"
                onError={hideOnError}
                className="object-cover"
              />
            </span>
          ))}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <h2
            id={titleId}
            className="type-title-lg sm:type-headline-md pr-8 text-neutral-900 min-[912px]:pr-0"
          >
            {name}
          </h2>
          {meta.length > 0 && (
            <p className="type-body-lg mt-[3px] flex flex-wrap items-center gap-1 text-neutral-500">
              {meta.map((part, i) => (
                <span key={part} className="contents">
                  {i > 0 && (
                    <span aria-hidden className="type-body-sm">
                      ·
                    </span>
                  )}
                  <span>{part}</span>
                </span>
              ))}
            </p>
          )}
          {price && (
            <p className="mt-1 flex flex-wrap items-baseline gap-x-2">
              <span className="type-title-lg whitespace-nowrap text-neutral-900">
                {price.now}
              </span>
              {price.was && (
                <s
                  title={price.wasTip}
                  className="type-body-lg whitespace-nowrap text-neutral-500"
                >
                  {price.wasTip && (
                    <span className="sr-only">{price.wasTip}: </span>
                  )}
                  {price.was}
                </s>
              )}
              {price.after && (
                <span className="type-body-lg text-neutral-500">
                  {price.after}
                </span>
              )}
            </p>
          )}
        </div>
      </div>

      {banner && (
        <div className="bg-light-blue-50 flex items-start gap-4 overflow-hidden rounded-2xl p-4">
          <InfoOutline className="text-light-blue-900 mt-0.5 size-5" />
          <p className="type-body-md text-neutral-900">{banner}</p>
        </div>
      )}

      <p className="type-body-lg-emphasized pr-4 text-neutral-900">
        {description}
      </p>

      {tiles.length > 0 && <Tiles tiles={tiles} cards={tileCards} />}

      {extra}

      {whatYouGet && whatYouGet.items.length > 0 && (
        <div className="flex flex-col gap-1">
          <SectionHeading>
            {whatYouGet.heading ?? t("piSectionWhatYouGet")}
          </SectionHeading>
          <ul className="flex flex-col">
            {whatYouGet.items.map((item) => (
              <li key={item} className="flex min-h-8 items-center gap-3">
                <CheckDisc />
                <span className="type-body-lg text-neutral-900">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {scanAreas && scanAreas.length > 0 && (
        <div className="flex flex-col gap-1">
          <SectionHeading>{t("piSectionAreasCovered")}</SectionHeading>
          <ul className="grid grid-cols-1 sm:grid-cols-2">
            {scanAreas.map((area) => (
              <li
                key={area}
                className="type-body-md flex h-9 items-center pr-4 text-neutral-900"
              >
                {area}
              </li>
            ))}
          </ul>
        </div>
      )}

      {biomarkers && biomarkers.length > 0 && (
        <Biomarkers
          groups={biomarkers}
          heading={t("piSectionBiomarkers")}
          more={t("piViewMore")}
          less={t("piViewLess")}
        />
      )}

      {info && info.length > 0 && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 py-2">
            <InfoOutline className="text-neutral-900" />
            <p className="type-title-md text-neutral-900">
              {t("piSectionInformation")}
            </p>
          </div>
          <dl className="flex flex-col gap-5 rounded-2xl bg-neutral-50 p-6">
            {info.map((row) => {
              const Icon = INFO_ICONS[row.key]
              return (
                <div key={row.key} className="flex items-start gap-4">
                  <span className="mt-0.5 shrink-0 text-neutral-900">
                    <Icon size={24} />
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col sm:flex-row sm:items-start sm:gap-8">
                    <dt className="type-title-md sm:type-body-lg text-neutral-900 sm:shrink-0">
                      {row.label}
                    </dt>
                    <dd className="type-body-lg text-neutral-900 sm:flex-1 sm:text-right">
                      {row.value}
                    </dd>
                  </div>
                </div>
              )
            })}
          </dl>
        </div>
      )}
    </div>
  )
}

/* ── The popup ─────────────────────────────────────────────────────────── */

/** True below the booking's 640px breakpoint, where the popup is a sheet. */
function usePhone() {
  const [phone, setPhone] = useState(false)
  useEffect(() => {
    const query = window.matchMedia("(max-width: 639px)")
    const update = () => setPhone(query.matches)
    update()
    query.addEventListener("change", update)
    return () => query.removeEventListener("change", update)
  }, [])
  return phone
}

/**
 * Locks the page while a popup is open, keeping the scrollbar's width, and
 * closes it on Escape. The latest `onClose` is read from a ref, so a caller's
 * inline handler does not re-lock the page on every render.
 */
function usePageLock(open: boolean, onClose: () => void) {
  const close = useRef(onClose)
  useEffect(() => {
    close.current = onClose
  })
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close.current()
    }
    const scrollbar = window.innerWidth - document.documentElement.clientWidth
    const { overflow, paddingRight } = document.body.style
    document.body.style.overflow = "hidden"
    if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`
    window.addEventListener("keydown", onKey)
    return () => {
      document.body.style.overflow = overflow
      document.body.style.paddingRight = paddingRight
      window.removeEventListener("keydown", onKey)
    }
  }, [open])
}

export type InfoLabels = {
  close: string
  add: string
  added: string
  /** The hover tips on the bookmark and on Save. */
  saveTip?: string
  savedTip?: string
}

/**
 * The details popup. On a desktop a card on the frosted page with the close
 * button beside it; on a phone a sheet from the bottom. Close and Add stay
 * at the foot while the copy scrolls. Add puts the item in the check-up and
 * turns black as "Added"; a second press takes it out, and the popup stays.
 */
export function InfoSheet({
  content,
  open,
  onClose,
  labels,
  action,
  tileCards = true,
}: {
  content: InfoContent
  open: boolean
  onClose: () => void
  labels: InfoLabels
  /** The Add button, for an item the page can add. */
  action?: { picked: boolean; onToggle: () => void; disabled?: boolean }
  /** Benefits as grey cards (a test) or as open sections (a package). */
  tileCards?: boolean
}) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const phone = usePhone()
  const titleId = useId()
  usePageLock(open, onClose)

  if (!mounted) return null

  const buttons = (
    <div className="flex gap-3 px-4 py-4 sm:gap-4 sm:px-8 sm:py-6">
      <button
        type="button"
        onClick={onClose}
        className="type-title-sm sm:type-title-md flex h-10 flex-1 items-center justify-center rounded-full bg-neutral-100 px-4 text-neutral-950 transition-colors hover:bg-neutral-200 sm:h-11 sm:px-8"
      >
        {labels.close}
      </button>
      {action && (
        <button
          type="button"
          aria-pressed={action.picked}
          disabled={action.disabled}
          onClick={action.onToggle}
          className={cn(
            "type-title-sm sm:type-title-md flex h-10 flex-1 items-center justify-center gap-2 rounded-full px-4 text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40 sm:h-11 sm:px-8",
            action.picked
              ? "bg-neutral-950 hover:bg-neutral-950/90"
              : "bg-deep-blue-900 hover:bg-deep-blue-900/90"
          )}
        >
          <BookmarkIcon filled={action.picked} className="size-4" />
          {action.picked ? labels.added : labels.add}
        </button>
      )}
    </div>
  )

  // Above the chat launcher (z-[9998]), which otherwise covers Add on a
  // phone, as the biomarkers drawer sits above it.
  return createPortal(
    /* safe-animate-presence: conditional-mount — child only renders when `open=true` */
    <AnimatePresence>
      {open &&
        (phone ? (
          <m.div key="sheet" className="fixed inset-0 z-[9999]">
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className={cn("fixed inset-0", FROST)}
              onClick={onClose}
            />
            <m.div
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="fixed inset-x-0 bottom-0 flex h-[92dvh] flex-col rounded-t-3xl bg-white shadow-2xl"
            >
              <button
                type="button"
                onClick={onClose}
                aria-label={labels.close}
                autoFocus
                className="absolute top-2.5 right-2.5 z-10 rounded-full p-2.5 text-neutral-400 hover:text-neutral-600"
              >
                <CloseIcon width={20} height={20} />
              </button>
              <div className="flex-1 overflow-y-auto overscroll-contain pt-16 pb-10">
                <InfoBody
                  content={content}
                  tileCards={tileCards}
                  titleId={titleId}
                />
              </div>
              <div className="relative bg-white">
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 -top-8 h-8 bg-gradient-to-t from-white to-transparent"
                />
                {buttons}
              </div>
            </m.div>
          </m.div>
        ) : (
          <m.div
            key="card"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { duration: 0.2, ease: "easeOut" },
            }}
            exit={{
              opacity: 0,
              transition: { duration: 0.15, ease: "easeIn" },
            }}
            className={cn(
              "fixed inset-0 z-[9999] overflow-y-auto overscroll-contain",
              FROST
            )}
            onClick={onClose}
          >
            <div className="flex min-h-full justify-center px-4 py-8">
              <m.div
                initial={{ opacity: 0, scale: 0.97, y: 12 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0,
                  transition: { duration: 0.3, ease: "easeOut" },
                }}
                exit={{
                  opacity: 0,
                  scale: 0.97,
                  y: 12,
                  transition: { duration: 0.15, ease: "easeIn" },
                }}
                className="relative h-fit w-full max-w-[800px]"
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby={titleId}
                  className="relative w-full rounded-3xl bg-white shadow-[0_0_20px_rgba(106,133,161,0.1),0_16px_48px_rgba(106,133,161,0.16)]"
                >
                  {/* Beside the card where there is room for it, as in the
                      booking; in the card's corner where there is not. */}
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label={labels.close}
                    autoFocus
                    className="absolute top-3 right-3 z-20 flex size-9 items-center justify-center rounded-full text-neutral-500 transition-colors hover:text-neutral-900 min-[912px]:top-0 min-[912px]:-right-[52px] min-[912px]:bg-white min-[912px]:text-neutral-900 min-[912px]:shadow-[0_0_20px_rgba(106,133,161,0.1),0_16px_48px_rgba(106,133,161,0.16)] min-[912px]:hover:text-neutral-600"
                  >
                    <CloseIcon width={20} height={20} />
                  </button>
                  <div className="pt-8 pb-4">
                    <InfoBody
                      content={content}
                      tileCards={tileCards}
                      titleId={titleId}
                    />
                  </div>
                  <div className="sticky bottom-0 z-10">
                    <div
                      aria-hidden
                      className="pointer-events-none h-8 bg-gradient-to-t from-white to-transparent"
                    />
                    <div className="rounded-b-3xl bg-white">{buttons}</div>
                  </div>
                </div>
              </m.div>
            </div>
          </m.div>
        ))}
    </AnimatePresence>,
    document.body
  )
}

/**
 * A choice popup in the booking's "Testing frequency" style: a grey card,
 * a title, then white option cards (see `ChoiceCard`). A card on the frosted
 * page with the close button beside it on a desktop, a sheet from the bottom
 * on a phone.
 */
export function ChoiceSheet({
  title,
  open,
  onClose,
  closeLabel,
  children,
}: {
  title: string
  open: boolean
  onClose: () => void
  closeLabel: string
  children: ReactNode
}) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const phone = usePhone()
  const titleId = useId()
  usePageLock(open, onClose)

  if (!mounted) return null

  const body = (
    <>
      {/* One white sheet, no card inside it: the title, then what the
          plan holds (Eugene, 08.10). */}
      <h2
        id={titleId}
        className="type-title-lg pr-10 font-semibold text-neutral-900 min-[752px]:pr-0"
      >
        {title}
      </h2>
      <div className="mt-5 flex flex-col">{children}</div>
    </>
  )

  return createPortal(
    /* safe-animate-presence: conditional-mount — child only renders when `open=true` */
    <AnimatePresence>
      {open &&
        (phone ? (
          <m.div key="sheet" className="fixed inset-0 z-[9999]">
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className={cn("fixed inset-0", FROST)}
              onClick={onClose}
            />
            <m.div
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="fixed inset-x-0 bottom-0 max-h-[92dvh] overflow-y-auto overscroll-contain rounded-t-3xl bg-white px-5 pt-8 pb-8 shadow-2xl"
            >
              <button
                type="button"
                onClick={onClose}
                aria-label={closeLabel}
                autoFocus
                className="absolute top-2.5 right-2.5 z-10 rounded-full p-2.5 text-neutral-500 hover:text-neutral-900"
              >
                <CloseIcon width={20} height={20} />
              </button>
              {body}
            </m.div>
          </m.div>
        ) : (
          <m.div
            key="card"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { duration: 0.2, ease: "easeOut" },
            }}
            exit={{
              opacity: 0,
              transition: { duration: 0.15, ease: "easeIn" },
            }}
            className={cn(
              "fixed inset-0 z-[9999] overflow-y-auto overscroll-contain",
              FROST
            )}
            onClick={onClose}
          >
            <div className="flex min-h-full items-center justify-center px-4 py-8">
              <m.div
                initial={{ opacity: 0, scale: 0.97, y: 12 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0,
                  transition: { duration: 0.3, ease: "easeOut" },
                }}
                exit={{
                  opacity: 0,
                  scale: 0.97,
                  y: 12,
                  transition: { duration: 0.15, ease: "easeIn" },
                }}
                className="relative h-fit w-full max-w-[480px]"
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby={titleId}
                  className="relative w-full rounded-3xl bg-white p-6 shadow-[0_0_20px_rgba(106,133,161,0.1),0_16px_48px_rgba(106,133,161,0.16)] sm:p-8"
                >
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label={closeLabel}
                    autoFocus
                    className="absolute top-3 right-3 z-20 flex size-9 items-center justify-center rounded-full text-neutral-500 transition-colors hover:text-neutral-900 min-[752px]:top-0 min-[752px]:-right-[52px] min-[752px]:bg-white min-[752px]:text-neutral-900 min-[752px]:shadow-[0_0_20px_rgba(106,133,161,0.1),0_16px_48px_rgba(106,133,161,0.16)] min-[752px]:hover:text-neutral-600"
                  >
                    <CloseIcon width={20} height={20} />
                  </button>
                  {body}
                </div>
              </m.div>
            </div>
          </m.div>
        ))}
    </AnimatePresence>,
    document.body
  )
}

/**
 * What a plan price holds, for a ChoiceSheet: a line on what it is, the
 * benefits with a check, then the price as a short receipt on a grey panel,
 * each part on its own row with the figure on the right and the total under
 * a rule, so the price a visitor saw outside is the first row (Eugene, 01.10
 * and 08.10). The sheet's button books it.
 */
export function PlanSummary({
  description,
  benefits,
  lines = [],
  total,
}: {
  description?: string
  /** A few short lines with a check: what the plan holds. */
  benefits?: string[]
  lines?: { label: string; note?: string; value: string }[]
  total: { label: string; value: string }
}) {
  return (
    <div>
      {description && (
        <p className="type-body-lg text-pretty text-neutral-600">
          {description}
        </p>
      )}
      {benefits && (
        <ul className={cn("flex flex-col gap-3", description && "mt-5")}>
          {benefits.map((benefit) => (
            <li
              key={benefit}
              className="type-body-lg flex items-start gap-3 text-neutral-900"
            >
              <CheckmarkIcon
                width={22}
                height={22}
                aria-hidden
                className="text-deep-green-500 mt-0.5 shrink-0"
              />
              {benefit}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-6 rounded-2xl bg-neutral-50 p-4 sm:p-5">
        {lines.length > 0 && (
          <div className="flex flex-col gap-2">
            {lines.map((line) => (
              <p
                key={line.label}
                className="type-body-md flex items-baseline justify-between gap-4"
              >
                <span>
                  <span className="text-neutral-900">{line.label}</span>
                  {line.note && (
                    <span className="text-neutral-500"> {line.note}</span>
                  )}
                </span>
                <span className="whitespace-nowrap text-neutral-900 tabular-nums">
                  {line.value}
                </span>
              </p>
            ))}
          </div>
        )}
        {lines.length > 0 && (
          <div aria-hidden className="my-3 h-px bg-neutral-200" />
        )}
        <p className="flex items-baseline justify-between gap-4">
          <span className="type-body-lg-emphasized text-neutral-900">
            {total.label}
          </span>
          <span className="type-title-md font-semibold whitespace-nowrap text-neutral-900 tabular-nums">
            {total.value}
          </span>
        </p>
      </div>
    </div>
  )
}
